import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, GetProp, UploadProps, Menu, Image, Grid } from 'antd';
import Dragger from 'antd/es/upload/Dragger';

import NotificationService from 'core/services/notification';
import { formatFileSize } from 'utils/FileSize';

import pdf from 'assets/images/pdf.png';
import Icon from 'utils/icon';

interface FileInputProps {
	name?: string;
	accept: 'photo' | 'pdf';
	src?: string;
	onSelect: (file: File) => void; // callback function to pass the file to the parent component
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const { useBreakpoint } = Grid;
function FileInput({ name, accept, onSelect, src }: FileInputProps) {
	const { t } = useTranslation();
	const screens = useBreakpoint();

	const [imageUrl, setImageUrl] = useState<string>();
	const [file, setFile] = useState<FileType>();

	const beforeUpload = (file: FileType) => {
		const isFileAccepted =
			accept == 'photo'
				? file.type === 'image/jpeg' || file.type === 'image/png'
				: accept == 'pdf'
				? file.type === 'application/pdf'
				: false;
		if (!isFileAccepted) {
			NotificationService.error(t(accept == 'photo' ? 'COMMON.ONLY_PNG_JPG' : 'COMMON.ONLY_PDF'));
			return false;
		}

		new Promise((resolve, reject) => {
			setFile(file);
			onSelect(file);
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(accept == 'photo' ? (reader.result as string) : pdf));
		});

		return false;
	};

	return (
		<Dragger
			style={{ textAlign: 'start' }}
			accept={accept == 'photo' ? 'image/png, image/jpeg, image/jpg' : '.pdf'}
			showUploadList={false}
			beforeUpload={beforeUpload}>
			<Flex justify="space-between" align="center" gap={!screens.md ? 5 : 20}>
				<Flex align="center" justify="start" gap={!screens.md ? 5 : 20} style={{ position: 'relative' }}>
					{file || src ? (
						<img
							src={imageUrl || src}
							style={{
								width: 50,
								height: 50,
								objectFit: 'cover',
								objectPosition: 'center',
							}}
						/>
					) : (
						<Flex align="center" style={{ height: 50 }}>
							<Icon name="upload" width={22} height={22} color="var(--primary)" />
						</Flex>
					)}
					<div>
						<p className="mb-0">{name ? name : file ? t('COMMON.UPLOAD_SUCCESSFUL') : t('COMMON.TAB_TO_UPLOAD')}</p>
						<span style={{ color: '#888', fontSize: 13 }}>
							{file && (
								<span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: 150, display: 'inline-block' }}>
									<Icon name="greenCheck" style={{ marginInlineEnd: 5 }} />
									{file.name}
								</span>
							)}
							{!file && `${accept == 'photo' ? 'PNG, JPG' : 'PDF'} | ${t('COMMON.MAX')}: 5 ${t('COMMON.MB')}`}
						</span>
					</div>
				</Flex>

				<Button size="middle" type={file ? 'link' : 'primary'}>
					{file && <Icon name="redo" width={16} height={16} />}
					{!file ? t('COMMON.UPLOAD') : screens.md ? t('COMMON.REUPLOAD') : ''}
				</Button>
			</Flex>
		</Dragger>
	);
}

export default FileInput;
